import {LoginSubmitButton,
  isWhitelabeled,
  LoginActions,
  SsoLogin,
  EmployeeZendeskRedirectLogin} from '/src/com/yext/platform/users/js/newloginhelpers/logincomponents';
import NewLoginTemplate from '/src/com/yext/platform/users/js/newloginhelpers/newlogintemplate';
import {registerComponentForJsonDataWithUIKit} from '/ui/lib/easyuikit';
import {AJAX_HEADER, checkResponseStatus} from '/ui/lib/fetch';
import {yext} from '/ui/lib/msg';

const {
  Box,
  FALCircleExclamation,
  FALCircleInfo,
  FALEnvelope,
  FALLockKeyhole,
  Flex,
  Form,
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  InputLeftElement,
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Stack,
  Text,
  VStack,
  Field,
} = require('@yext/mana-ui');

const SECTIONS = {
  USERNAME: 'USERNAME',
  SSO: 'SSO',
  PASSWORD: 'PASSWORD',
  ACCOUNT_ID: 'ACCOUNT_ID',
  EMPLOYEE_LOGIN_CHOICE: 'EMPLOYEE_LOGIN_CHOICE',
};

export function NewPartitionLogin({
  whitelabelInfo,
  username,
  rememberMe,
  global,
  b,
  reauth,
  passwordOnly,
  redirectUrl,
  isMobileRedirect,
  isAllState,
}) {
  const [section, setSection] = React.useState(SECTIONS.USERNAME);
  const [isInEmployeeFlow, setIsInEmployeeFlow] = React.useState(false);

  const getSubtitle = () => {
    if (reauth) {
      return yext.msg('In order to update users, you will need to verify your identity again.');
    } else if (isAllState) {
      return null;
    } else {
      return yext.msg('Please sign in to continue.');
    }
  };

  const getTitle = () => {
    if (reauth) {
      return '';
    } else {
      return isWhitelabeled(whitelabelInfo)
        ? isAllState
          ? ''
          : yext.msg('Welcome to {0}', whitelabelInfo.name)
        : yext.msg('Welcome to Yext');
    }
  };

  return (
    <NewLoginTemplate
      title={getTitle()}
      subtitle={getSubtitle()}
      cardContent={
        <CardContent
          whitelabelInfo={whitelabelInfo}
          initialUsername={username}
          initialRememberMe={rememberMe}
          reauth={reauth}
          global={global}
          b={b}
          passwordOnly={passwordOnly}
          redirectUrl={redirectUrl}
          section={section}
          setSection={setSection}
          setIsInEmployeeFlow={setIsInEmployeeFlow}
        />}
      whitelabelInfo={whitelabelInfo}
      username={username}
      pageType={'login'}
      isMobileRedirect={isMobileRedirect}
      showBackToUsername={section !== SECTIONS.USERNAME && !reauth}
      isAllState={isAllState}
      onBackClick={() => {
        if (section === SECTIONS.PASSWORD && isInEmployeeFlow) {
          setSection(SECTIONS.EMPLOYEE_LOGIN_CHOICE);
        } else {
          setSection(SECTIONS.USERNAME);
          setIsInEmployeeFlow(false);
        }
      }}
    />
  );
}

function CardContent({
  whitelabelInfo,
  initialUsername,
  initialRememberMe,
  global,
  b,
  reauth,
  passwordOnly,
  redirectUrl,
  section,
  setSection,
  setIsInEmployeeFlow,
}) {
  // Error message to be displayed
  const [error, setError] = React.useState('');
  const [isSubmitBtnDisabled, setIsSubmitBtnDisabled] = React.useState(false);
  const [username, setUsername] = React.useState(initialUsername);
  // Track the initial value for the username and password sections
  const [rememberMe, setRememberMe] = React.useState(initialRememberMe);
  const [loginTypes, setLoginTypes] = React.useState([]);
  const shouldShowSsoLogin = !isWhitelabeled(whitelabelInfo) && !passwordOnly;

  // check if email is a regular Yext employee
  const isRegularYextEmployee = email =>
    b && email && email.toLowerCase().trim().endsWith('@yext.com') && !email.includes('+');

  // build partition employee login URL
  const buildEmployeeLoginUrl = (username, businessId) => {
    const params = [];
    if (username) params.push(`username=${encodeURIComponent(username)}`);
    if (businessId) params.push(`b=${businessId}`);
    if (redirectUrl) params.push(`c=${encodeURIComponent(redirectUrl)}`);
    return '/users/employeeLoginFromPartition' + (params.length > 0 ? '?' + params.join('&') : '');
  };

  // handle regular login flow
  const handleRegularLogin = username => {
    const request = new FormData();
    request.append('username', username);
    request.append('b', b);
    if (redirectUrl) request.append('c', redirectUrl);

    fetch('/users/getLoginType', {
      method: 'POST',
      headers: AJAX_HEADER,
      body: request,
    })
      .then(checkResponseStatus)
      .then(response => response.json())
      .then(data => determineUserLoginType(data))
      .finally(() => setIsSubmitBtnDisabled(false));
  };


  React.useEffect(() => {
    if ((global || reauth) && username) {
      // If it is a redirect from global login, and there is a username included,
      // and it is at the username submission stage of the login flow, submit username
      submitUsername({username, rememberMe});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Make API call to get a list of login types for a given username. */
  const submitUsername = formData => {
    setError('');
    if (formData['username'].trim() === '') {
      setError(yext.msg('Please enter a username.'));
      return;
    }
    setUsername(formData['username']);
    setIsSubmitBtnDisabled(true);

    if (formData['rememberMe'] !== undefined) {
      setRememberMe(formData['rememberMe']);
    }

    const username = formData['username'];

    // Show employee choice for @yext.com emails on partition login.
    if (isRegularYextEmployee(username)) {
      setIsInEmployeeFlow(true);
      setSection(SECTIONS.EMPLOYEE_LOGIN_CHOICE);
      setIsSubmitBtnDisabled(false);
    } else {
      handleRegularLogin(username);
    }
  };

  /** Make API call to submit login */
  const submitLogin = formData => {
    setError('');
    if (formData['password'].trim() === '') {
      setError(yext.msg('Please enter a password.'));
      return;
    }
    setIsSubmitBtnDisabled(true);

    if (formData['rememberMe'] !== undefined) {
      setRememberMe(formData['rememberMe']);
    }

    const data = new FormData();
    data.append('username', username);
    data.append('password', formData['password']);
    const rememberMeValue = formData['rememberMe'] !== undefined
      ? formData['rememberMe']
      : rememberMe;
    data.append('rememberMe', rememberMeValue);
    data.append('reauth', reauth);
    let url = '/users/partition/login';
    // maintain the same redirectURL upon form submission in case the password is incorrect
    if (redirectUrl) {
      url = url + '?c=' + encodeURIComponent(redirectUrl + window.location.hash);
    }
    fetch(url, {
      method: 'POST',
      headers: AJAX_HEADER,
      body: data,
    })
      .then(checkResponseStatus)
      .then(response => response.json())
      .then(json => {
        if (json.error) {
          if (json.error === 'noProductAccess') {
            window.location.href = '/users/noproductaccess';
          } else {
            if (!reauth) {
              setSection(SECTIONS.USERNAME);
            }
            setError(json.error);
          }
        } else if (json.success) {
          window.location.href = json.success;
        }
      })
      .finally(() => setIsSubmitBtnDisabled(false));
  };

  const submitInitiateSso = b => {
    let url = '/users/initiateSso?b=' + b;
    if (redirectUrl) {
      const destination = redirectUrl.includes('#')
        ? redirectUrl
        : redirectUrl + window.location.hash;
      url += '&c=' + encodeURIComponent(destination);
    }
    window.location.href = url;
  };


  /**
   * Given a list of loginTypes, determine the next phase of the login flow.
   *
   * Note: (APV-573) We should never return 0 login types.
   *
   * If there is just one user, proceed with the login flow based on login type
   * Else if there are multiple users,
   *   if all of those users belong to the same account and at least one of
   *   them is a password user, proceed as if it were a password user.
   *   else, ask for account ID to determine the user.
   * @param {!Array<*>} loginTypes
   */
  const determineUserLoginType = loginTypes => {
    if (loginTypes.length === 1) {
      switch (loginTypes[0]['loginType']) {
        case 'SSO_LOGIN':
          submitInitiateSso(loginTypes[0]['businessId']);
          break;
        case 'PASSWORD':
          setSection(SECTIONS.PASSWORD);
          break;
      }
    } else {
      if (shouldUsePasswordLogin(loginTypes)) {
        setSection(SECTIONS.PASSWORD);
      } else {
        setLoginTypes(loginTypes);
        setSection(SECTIONS.ACCOUNT_ID);
      }
    }
  };

  const onSSOClick = () => submitInitiateSso(b);

  function UsernameSection() {
    return (
      <FormControl>
        <Form onSubmit={submitUsername}>
          {() => (
            <>
              <VStack
                spacing={5}
                align={'stretch'}
              >
                <FormControl isInvalid={error?.trim()}>
                  <Field
                    name={'username'}
                    type={'text'}
                    variant={'gray'}
                    label={yext.msg('Username')}
                    placeholder={'john@abccompany.com'}
                    isRequired={true}
                    defaultValue={username}
                    icon={<FALEnvelope/>}
                  />
                  <LoginError />
                </FormControl>
                <VStack
                  spacing={4}
                  align={'stretch'}
                >
                  <LoginSubmitButton whitelabelInfo={whitelabelInfo} isDisabled={isSubmitBtnDisabled}>
                    {yext.msg('Next')}
                  </LoginSubmitButton>
                  <LoginActions
                    initialRememberMe={rememberMe}
                  />
                  <EmployeeZendeskRedirectLogin redirectUrl={redirectUrl} />
                  <SsoLogin
                    onClick={onSSOClick}
                    shouldShowSsoLogin={shouldShowSsoLogin}
                  />
                </VStack>
              </VStack>
            </>
          )}
        </Form>
      </FormControl>
    );
  }

  function SSOSection() {
    return (
      <FormControl>
        <Form onSubmit={submitUsername}>
          {() => (
            <>
              <VStack
                spacing={5}
                align={'stretch'}
              >
                <Field
                  name={'username'}
                  type={'text'}
                  variant={'gray'}
                  label={yext.msg('Username')}
                  placeholder={'john@abccompany.com'}
                  isRequired={true}
                  defaultValue={username}
                  icon={<FALEnvelope/>}
                />
                <LoginSubmitButton whitelabelInfo={whitelabelInfo} isDisabled={isSubmitBtnDisabled}>
                  {yext.msg('Next')}
                </LoginSubmitButton>
              </VStack>
            </>
          )}
        </Form>
      </FormControl>
    );
  }

  function PasswordSection() {
    return (
      <FormControl>
        <Form onSubmit={submitLogin}>
          {() => (
            <>
              <VStack
                spacing={5}
                align={'stretch'}
              >
                <FormControl isInvalid={error?.trim()}>
                  <Field
                    name={'password'}
                    type={'password'}
                    variant={'gray'}
                    label={yext.msg('Password')}
                    isRequired={true}
                    leftAddon={<InputLeftElement><FALLockKeyhole/></InputLeftElement>}
                  />
                  <LoginError />
                </FormControl>
                <VStack
                  spacing={4}
                  align={'stretch'}
                >
                  <LoginSubmitButton whitelabelInfo={whitelabelInfo} isDisabled={isSubmitBtnDisabled}>
                    {reauth ? yext.msg('Verify') : yext.msg('Sign In')}
                  </LoginSubmitButton>
                  <LoginActions
                    initialRememberMe={rememberMe}
                  />
                </VStack>
              </VStack>
            </>)}
        </Form>
      </FormControl>
    );
  }

  /**
     * This method is used when there are multiple SSO users, then the user submits the account ID.
     * If the business ID matches an SSO user, initiate SSO login.
     */
  const onAccountIdSubmit = ({accountId, rememberMe}) => {
    if (rememberMe !== undefined) {
      setRememberMe(rememberMe);
    }

    const loginTypesOfAccount =
        loginTypes.filter(
          type => type['businessId']
                && type['businessId'].toString() === accountId.toString());

    if (shouldUsePasswordLogin(loginTypesOfAccount)) {
      setSection(SECTIONS.PASSWORD);
      return;
    } else {
      for (const type of loginTypesOfAccount) {
        if (type['loginType'] === 'SSO_LOGIN') {
          submitInitiateSso(accountId);
          return;
        }
      }
    }
    setError('Invalid Login');
  };

  function AccountIdSection() {
    return (
      <Form onSubmit={onAccountIdSubmit}>
        {() => (
          <>
            <VStack
              spacing={5}
              align={'stretch'}
            >
              <FormControl isInvalid={error?.trim()}>
                <Field
                  name={'accountId'}
                  type={'text'}
                  variant={'gray'}
                  label={yext.msg('Account ID')}
                  isRequired={true}
                />
                <LoginError />
              </FormControl>
              <VStack
                spacing={4}
                align={'stretch'}
              >
                <LoginSubmitButton whitelabelInfo={whitelabelInfo} isDisabled={isSubmitBtnDisabled}>
                  {yext.msg('Next')}
                </LoginSubmitButton>
                <LoginActions
                  initialRememberMe={rememberMe}
                />
                <EmployeeZendeskRedirectLogin redirectUrl={redirectUrl} />
                <SsoLogin
                  onClick={onSSOClick}
                  shouldShowSsoLogin={shouldShowSsoLogin}
                />
              </VStack>
            </VStack>
          </>
        )}
      </Form>
    );
  }

  function EmployeeLoginChoiceSection() {
    const [loginOption, setLoginOption] = React.useState('');

    const renderTooltip = (title, description) => (
      <Popover
        placement="right"
        trigger="hover"
        gutter={8}
        flip={false}
        preventOverflow={false}
      >
        <PopoverTrigger>
          <IconButton
            aria-label={yext.msg('More information')}
            icon={<FALCircleInfo width="16px" height="14px"/>}
            size="xs"
            variant="ghost"
            minWidth="20px"
            minHeight="2px"
            width="20px"
            height="20px"
            padding={0}
            borderRadius="full"
            _hover={{
              bg: 'surfaces.alpha.dominant.50',
            }}
          />
        </PopoverTrigger>
        <PopoverContent
          bg="surfaces.highlight.secondary"
          borderColor="borders.secondary.soft.resting"
          borderRadius="lg"
          padding={6}
          maxWidth="320px"
        >
          <PopoverArrow bg="surfaces.highlight.secondary" borderColor="borders.secondary.soft.resting" />
          <HStack gap={1} alignItems="flex-start" width="100%">
            <Box flex={1}>
              <Text fontWeight="600" fontSize="lg" color="text.neutralDominant" mb={2}>{title}</Text>
              <Text fontSize="md" color="text.neutralDominant" lineHeight="21px">{description}</Text>
            </Box>
            <PopoverCloseButton
              size="md"
              position="static"
              marginLeft={1}
            />
          </HStack>
        </PopoverContent>
      </Popover>
    );

    const handleEmployeeLoginChoice = () => {
      if (!loginOption) {
        setError(yext.msg('Please select a login option.'));
        return;
      }

      if (loginOption === 'employee') {
        // Redirect to employee login endpoint
        window.location.href = buildEmployeeLoginUrl(username, b);
      } else {
        // Log in as regular user
        handleRegularLogin(username);
      }
    };

    return (
      <Form onSubmit={handleEmployeeLoginChoice}>
        {() => (
          <VStack spacing={5} align={'stretch'}>
            <FormControl as="fieldset" isRequired isInvalid={error?.trim()}>
              <Box as="legend" mb={5} fontSize="md" fontWeight={500}>
                {yext.msg('Login Options')} <Box as="span" color="red.500">*</Box>
              </Box>
              <RadioGroup value={loginOption} onChange={setLoginOption}>
                <Stack direction="column" spacing={5}>
                  <Radio value="employee" size="md">
                    <Flex align="center" gap={1.5}>
                      <Text fontSize="md">{yext.msg('Log in as employee')}</Text>
                      {renderTooltip(
                        yext.msg('Log in as employee'),
                        yext.msg('Continue authenticating as an employee via SSO'),
                      )}
                    </Flex>
                  </Radio>
                  <Radio value="accountUser" size="md">
                    <Flex align="center" gap={1.5}>
                      <Text fontSize="md">{yext.msg('Log in as account user')}</Text>
                      {renderTooltip(
                        yext.msg('Log in as account user'),
                        yext.msg('Continue authenticating as a specific user in this account via password'),
                      )}
                    </Flex>
                  </Radio>
                </Stack>
              </RadioGroup>
              {error?.trim() && <LoginError />}
            </FormControl>

            <LoginSubmitButton whitelabelInfo={whitelabelInfo} isDisabled={!loginOption}>
              {yext.msg('Next')}
            </LoginSubmitButton>
          </VStack>
        )}
      </Form>
    );
  }

  function LoginError() {
    return (
      <Flex mt={2} justifyContent={'space-between'} alignItems={'center'}>
        <Box display={'flex'} alignItems={'center'}>
          <FormErrorMessage>
            <FALCircleExclamation me={1.5}/>
            {error}
          </FormErrorMessage>
        </Box>
      </Flex>
    );
  }

  switch (section) {
    case SECTIONS.USERNAME:
      return <UsernameSection/>;
    case SECTIONS.SSO:
      return <SSOSection/>;
    case SECTIONS.PASSWORD:
      return <PasswordSection/>;
    case SECTIONS.ACCOUNT_ID:
      return <AccountIdSection/>;
    case SECTIONS.EMPLOYEE_LOGIN_CHOICE:
      return <EmployeeLoginChoiceSection/>;
    default:
      return null;
  }
}

/**
 * If a given list of login types are all for the same account, and at least
 * one of them is a password user, use the password login flow.
 *
 * @param {!Array<*>} loginTypes
 */
const shouldUsePasswordLogin = loginTypes => {
  if (loginTypes.length === 0) {
    return false;
  }

  const businessId = loginTypes[0]['businessId'];
  let allLoginTypesInSameBusiness = true;
  let includesPasswordLoginType = false;
  for (const type of loginTypes) {
    if (businessId !== type['businessId']) {
      allLoginTypesInSameBusiness = false;
    }
    if (type['loginType'] === 'PASSWORD') {
      includesPasswordLoginType = true;
    }
  }
  return allLoginTypesInSameBusiness && includesPasswordLoginType;
};

document.addEventListener('DOMContentLoaded', () => {
  registerComponentForJsonDataWithUIKit('NewPartitionLogin', NewPartitionLogin);
});
